// Module 08 — worker public entry (sim-worker.v1). Implements ROADMAP §11. Two halves of one
// protocol: runWorker() is the worker-side message pump (binds a scope's onmessage to the tagged
// union, owns the single authoritative clock + a return-trip buffer pool); createSimWorkerClient()
// is the thin main-thread proxy implementing SimWorkerApi over a Worker-like channel. Both are
// transport-injectable so the determinism + protocol are unit-testable in Node (a real
// type:'module' Worker can't run under vitest). The only file that touches `self`/`fetch` is
// sim.worker.ts. Boundary (sim-worker.v1): import only this module, `domain`, `topology`,
// `engine`, or npm. Frozen interface: docs/interfaces/sim-worker.v1.md · Rule: .claude/rules/worker-state.md
import type { Command, Snapshot, NodeId, EdgeId } from "../domain/index";
import { createEngine as defaultCreateEngine } from "../engine/index";
import type { Engine, SimModel } from "../engine/index";
import { SimClock, intervalMsForSpeed } from "./loop";
import type { Scheduler } from "./loop";
import { BufferPool, packSnapshot, unpackSnapshot } from "./protocol";
import type { MainToWorker, WorkerToMain, PackContext } from "./protocol";

// Re-exports (the module's public surface).
export { SimClock, intervalMsForSpeed, timerScheduler } from "./loop";
export type { Scheduler } from "./loop";
export { BufferPool, packSnapshot, unpackSnapshot } from "./protocol";
export type { MainToWorker, WorkerToMain, PackedSnapshot, SnapshotMeta, PackContext } from "./protocol";
export { buildSimModel, SLACK_BUS_ID, IMPORT_CAP_MW } from "./model";
export type { ModelConfig } from "./model";

// The logical API (sim-worker.v1). The client below implements it; the UI/state (Module 09) consume it.
export interface SimWorkerApi {
  init(modelUrl: string): Promise<void>;
  start(speed: number): void;
  stop(): void;
  setSpeed(speed: number): void;
  postCommand(cmd: Command): void;
  subscribe(onSnapshot: (s: Snapshot) => void): void;
}

// ── worker side ────────────────────────────────────────────────────────────────
export interface WorkerScope {
  postMessage(msg: WorkerToMain, transfer?: Transferable[]): void;
  onmessage: ((ev: { data: MainToWorker }) => void) | null;
}

export interface WorkerDeps {
  loadModel: (modelUrl: string) => Promise<SimModel>; // injectable I/O (sim.worker.ts does the fetch)
  scheduler: Scheduler;
  createEngine?: () => Engine;                          // defaults to the engine.v1 facade
  pool?: BufferPool;
  deriveContext?: (model: SimModel) => PackContext;     // index→id ordering for packing
}

function defaultContext(model: SimModel): PackContext {
  return {
    nodeIds: model.nodes.map((n): NodeId => n.id),
    edgeIds: model.edges.map((e): EdgeId => e.id),
  };
}

function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export function runWorker(scope: WorkerScope, deps: WorkerDeps): void {
  const pool = deps.pool ?? new BufferPool();
  const makeEngine = deps.createEngine ?? defaultCreateEngine;
  const deriveContext = deps.deriveContext ?? defaultContext;
  const { scheduler } = deps;

  let clock: SimClock | null = null;
  let ctx: PackContext | null = null;

  const emitSnapshot = (s: Snapshot): void => {
    if (ctx === null) return;
    const { packed, transfer } = packSnapshot(s, ctx, pool);
    scope.postMessage({ t: "snapshot", packed }, transfer);
  };

  let paused = true; // scheduler stopped ⇒ no ticks fire; a command must re-solve the current hour itself
  const setRate = (speed: number): void => {
    const ms = intervalMsForSpeed(speed);
    if (ms > 0 && clock !== null) { paused = false; scheduler.start(ms, () => clock!.runTick()); }
    else { paused = true; scheduler.stop(); } // non-positive speed (or no clock yet) ⇒ paused
  };

  const handleInit = async (modelUrl: string): Promise<void> => {
    try {
      const model = await deps.loadModel(modelUrl);
      const engine = makeEngine();
      engine.init(model);
      ctx = deriveContext(model);
      clock = new SimClock(engine, emitSnapshot);
      scope.postMessage({ t: "ready" });
    } catch (e) {
      scope.postMessage({ t: "error", message: errorMessage(e) });
    }
  };

  scope.onmessage = (ev): void => {
    const msg = ev.data;
    switch (msg.t) {
      case "init":
        void handleInit(msg.modelUrl);
        return;
      case "start":
      case "setSpeed":
        setRate(msg.speed);
        return;
      case "stop":
        paused = true;
        scheduler.stop();
        return;
      case "command":
        clock?.enqueue(msg.cmd);
        // PAUSED edits must be visible immediately: re-solve the current hour + emit one snapshot now
        // (a paused scheduler never drains the queue otherwise). While running, the next tick drains it.
        if (paused) clock?.resolveNow();
        return;
      case "recycle":
        for (const buf of msg.buffers) pool.release(buf);
        return;
    }
  };
}

// ── main-thread proxy ────────────────────────────────────────────────────────────
export interface WorkerLike {
  postMessage(msg: MainToWorker, transfer?: Transferable[]): void;
  onmessage: ((ev: { data: WorkerToMain }) => void) | null;
}

export interface SimWorkerClientDeps {
  worker: WorkerLike;
  context: PackContext; // the same static node/edge ordering the worker packs against
}

export function createSimWorkerClient(deps: SimWorkerClientDeps): SimWorkerApi {
  const { worker, context } = deps;
  const subscribers: ((s: Snapshot) => void)[] = [];
  let resolveInit: (() => void) | null = null;
  let rejectInit: ((e: Error) => void) | null = null;

  worker.onmessage = (ev): void => {
    const msg = ev.data;
    switch (msg.t) {
      case "ready":
        resolveInit?.();
        resolveInit = rejectInit = null;
        return;
      case "error":
        rejectInit?.(new Error(msg.message));
        resolveInit = rejectInit = null;
        return;
      case "snapshot": {
        const snap = unpackSnapshot(msg.packed, context);
        for (const cb of subscribers) cb(snap);
        // return the consumed buffers to the worker's pool (the return-trip half of the pool).
        const buffers = [msg.packed.nodeAngles.buffer, msg.packed.busVMagPu.buffer, msg.packed.branchFlowMw.buffer,
          msg.packed.branchLoadingPct.buffer, msg.packed.branchEnergized.buffer,
          msg.packed.branchInService.buffer, msg.packed.branchContingencyPct.buffer,
          msg.packed.branchIslandId.buffer, msg.packed.branchFlowMag.buffer, msg.packed.branchFlowDir.buffer];
        worker.postMessage({ t: "recycle", buffers }, buffers);
        return;
      }
    }
  };

  return {
    init(modelUrl: string): Promise<void> {
      return new Promise<void>((resolve, reject) => {
        resolveInit = resolve;
        rejectInit = reject;
        worker.postMessage({ t: "init", modelUrl });
      });
    },
    start(speed: number): void { worker.postMessage({ t: "start", speed }); },
    stop(): void { worker.postMessage({ t: "stop" }); },
    setSpeed(speed: number): void { worker.postMessage({ t: "setSpeed", speed }); },
    postCommand(cmd: Command): void { worker.postMessage({ t: "command", cmd }); },
    subscribe(onSnapshot: (s: Snapshot) => void): void { subscribers.push(onSnapshot); },
  };
}
